/* eslint-disable react/prop-types */
import React, { useEffect, useState, useContext } from 'react';
import { DashboardModalActions } from '@carvana/purchase-helpers';
import moment from 'moment';
import { CheckCircle } from '@carvana/purchase-icons';
import { CheckboxNext } from '@carvana/purchase-styles';
import { EventTrackingActions } from '@carvana/purchase-utils';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import * as AppActions from '../../ducks/actions';
import * as sc from './cancel-order-modal.styles';
import PropTypes from 'prop-types';

const CancelOrderConfirm = ({
  setIsCancelled,
  method,
  shippingFee,
  orderPlacedOnUtc,
  contractsSigned,
  customerIsGreen,
  isNRSF
}) => {
  const [selected, setSelected] = useState('');
  const [otherText, setOtherText] = useState('');
  const { trackEvent } = useContext(AnalyticsContext);

  const selectedOption = {
    cancelOption: selected,
    otherText: selected === 'Other' ? otherText : ''
  };

  const shippingFeeNullCheck = shippingFee ?? 0;
  const isInNrsfExperiment = isNRSF ?? false;

  const hoursSinceOrderPlaced = moment(new Date()).diff(
    orderPlacedOnUtc,
    'hours'
  );

  const formatMoney = number =>
    number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });

  const shippingFeeFormatted = formatMoney(shippingFeeNullCheck);

  const isCash = method === 'Cash';
  const isFinance = method === 'Financing';
  const isSaf = method === 'ThirdParty';

  const confirmCancelOrder = () => {
    EventTrackingActions.TrackEvent({
      name: 'Cancel option selected',
      data: selectedOption
    });
    trackEvent('v2_opd_cancel_order_button_tap');
    DashboardModalActions.ShowModalLoadingSpinner();
    AppActions.CancelOrder()
      .then(() => trackEvent('pp_clicked_cancel_purchase'))
      .then(() => setIsCancelled(true));
  };

  const handleChange = event => setOtherText(event.target.value);

  const Options = [
    'Decided not to buy a car right now',
    'Switching to another Carvana car',
    'No longer fits my budget',
    'Delivery Date has been delayed',
    'Poor customer experience',
    'Found a car elsewhere',
    'Other'
  ];

  return (
    <>
      <sc.AlertIcon />
      <sc.BoldText>Are you sure you want to cancel your order?</sc.BoldText>
      <sc.BodyText>
        This vehicle will be immediately available for other buyers and cannot
        be undone.
      </sc.BodyText>
      {((isCash && shippingFeeNullCheck > 0) ||
        (isFinance &&
          customerIsGreen &&
          contractsSigned &&
          shippingFeeNullCheck > 0 &&
          isInNrsfExperiment) ||
        (isSaf && shippingFeeNullCheck > 0 && isInNrsfExperiment)) &&
        hoursSinceOrderPlaced >= 24 && (
          <sc.BodyText>
            Your <b>{shippingFeeFormatted}</b> shipping fee may not be eligible
            for a refund.
          </sc.BodyText>
        )}
      <sc.BodyText>Select a reason:</sc.BodyText>
      <sc.OptionsWrapper>
        {Options.map(option => {
          return (
            <sc.Option>
              <CheckboxNext
                onChange={() => setSelected(option)}
                round
                value={selected === option}
                text={option}
              />
            </sc.Option>
          );
        })}

        {selected === 'Other' && (
          <sc.OptionText>
            <form>
              <sc.TextAreaStyle
                value={otherText}
                onChange={handleChange}
                rows={4}
                placeholder="Add a message here"
                maxLength={250}
              />
            </form>
          </sc.OptionText>
        )}
      </sc.OptionsWrapper>
      <sc.ButtonWrapper>
        <sc.ConfirmButton
          onClick={confirmCancelOrder}
          disabled={selected.length === 0}
        >
          Yes, cancel
        </sc.ConfirmButton>
        <sc.CancelButton onClick={DashboardModalActions.CloseModal}>
          No, go back
        </sc.CancelButton>
      </sc.ButtonWrapper>
    </>
  );
};

const redirectToInventory = () => {
  window.location.href = `${process.env.BASE_URL}/cars`;
};

const CancelOrderSuccess = () => {
  useEffect(() => {
    DashboardModalActions.HideModalLoadingSpinner({
      modalConfig: {
        closeModalCallback: redirectToInventory,
        headerText: 'ORDER CANCELLED'
      }
    });
  }, []);

  return (
    <>
      <CheckCircle size="50px" />
      <sc.BoldText>Your order has been cancelled.</sc.BoldText>
      <sc.BodyText>
        Sorry this one didn&apos;t work out. If you&apos;re still looking for a
        car, check out our other vehicles.
      </sc.BodyText>
      <sc.ButtonWrapper>
        <sc.CancelButton onClick={DashboardModalActions.CloseModal}>
          Search vehicles
        </sc.CancelButton>
      </sc.ButtonWrapper>
    </>
  );
};

const CancelOrderModal = props => {
  const {
    contractsSigned,
    customerIsGreen,
    isNRSF,
    orderSummary: {
      paymentMethod: { method } = {},
      quote: { deliveryCharge: { amount } = {} } = {},
      orderPlacedOnUtc
    }
  } = props;
  const [isCancelled, setIsCancelled] = useState(false);

  return (
    <sc.ContentWrapper>
      {isCancelled ? (
        <CancelOrderSuccess />
      ) : (
        <CancelOrderConfirm
          setIsCancelled={setIsCancelled}
          method={method}
          shippingFee={amount}
          orderPlacedOnUtc={orderPlacedOnUtc}
          contractsSigned={contractsSigned}
          customerIsGreen={customerIsGreen}
          isNRSF={isNRSF}
        />
      )}
    </sc.ContentWrapper>
  );
};

CancelOrderModal.propTypes = {
  contractsSigned: PropTypes.bool,
  customerIsGreen: PropTypes.bool,
  isNRSF: PropTypes.bool,
  orderSummary: PropTypes.shape({
    paymentMethod: PropTypes.shape({ method: PropTypes.string }),
    quote: PropTypes.shape({
      deliveryCharge: PropTypes.shape({ amount: PropTypes.number })
    }),
    orderPlacedOnUtc: PropTypes.string
  })
};

CancelOrderModal.defaultProps = {
  contractsSigned: false,
  customerIsGreen: false,
  isNRSF: false,
  orderSummary: {
    paymentMethod: { method: '' },
    quote: { deliveryCharge: { amount: 0 } },
    orderPlacedOnUtc: ''
  }
};

export default CancelOrderModal;
